import { useEffect, useRef, useState } from 'react'
import { uploadAttachments } from '../../../services/comments'
import { GuidePicker } from '../../guides/GuidePicker'
import MultiSelect from '../../inputs/MultiSelect'
import { topicLabelFor } from '../../../utils/topicLabel'

const COMPOSER_OPEN_KEY = 'ct-comments-composer-open'

const IconChevron = ({ up }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {up ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
  </svg>
)

const IconPaperclip = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
)

// Text + flags composer. For top-level comments (`showItems`) it also shows the
// tagged-item chips and a "Tag items" button; replies omit item tagging. Both
// support file attachments (images / video / PDF / any file): files are uploaded
// on submit and their descriptors are passed to onSubmit as `attachments`.
export function CommentComposer({
  onSubmit,
  placeholder = 'Write a comment…',
  submitLabel = 'Post',
  compact = false,
  showItems = false,
  items = [],
  onEditItems,
  onRemoveItem,
  topicOptions = [],
}) {
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [isQuestion, setIsQuestion] = useState(false)
  const [isPublic, setIsPublic] = useState(false)
  const [files, setFiles] = useState([])
  const [guides, setGuides] = useState([])
  const [topics, setTopics] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const canCollapse = showItems && !compact
  const [open, setOpen] = useState(() => {
    if (!canCollapse) return true
    try {
      return localStorage.getItem(COMPOSER_OPEN_KEY) !== '0'
    } catch {
      return true
    }
  })
  const ref = useRef(null)
  const fileRef = useRef(null)

  const resize = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }

  useEffect(() => {
    resize()
  }, [])

  useEffect(() => {
    if (open) resize()
  }, [open])

  const toggleOpen = () => {
    setOpen((value) => {
      const next = !value
      try {
        localStorage.setItem(COMPOSER_OPEN_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const addFiles = (list) => {
    const picked = Array.from(list || [])
    if (picked.length) setFiles((prev) => [...prev, ...picked])
  }

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index))

  const canSend = text.trim() && !(showItems && items.length === 0)

  const send = async () => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    if (showItems && items.length === 0) return
    setBusy(true)
    setError('')
    try {
      let attachments
      if (files.length) attachments = await uploadAttachments(files)
      await onSubmit?.({
        title: title.trim(),
        text: trimmed,
        isQuestion,
        isPublic,
        attachments,
        guides,
        topics,
      })
      setTitle('')
      setText('')
      setIsQuestion(false)
      setFiles([])
      setGuides([])
      setTopics([])
      requestAnimationFrame(resize)
    } catch (err) {
      setError(err.message || 'Could not post. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const fields = (
    <>
      {showItems ? (
        <div className="cmt-compose-items">
          <div className="cmt-compose-items-chips">
            {items.map((it) => (
              <span key={it._id} className="cmt-tag">
                <span className="cmt-tag-label" title={it.label}>
                  {it.label}
                </span>
                {items.length > 1 && (
                  <button
                    type="button"
                    className="cmt-tag-x"
                    title="Remove tag"
                    aria-label={`Remove ${it.label}`}
                    onClick={() => onRemoveItem?.(it)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
          <button type="button" className="cmt-tag-add" onClick={onEditItems} disabled={busy}>
            Tag items
          </button>
        </div>
      ) : null}

      {showItems ? (
        <div className="cmt-compose-topics">
          <MultiSelect
            value={topics}
            onChange={setTopics}
            options={topicOptions}
            getOptionValue={(t) => t._id}
            getOptionLabel={topicLabelFor(topicOptions)}
            placeholder="No topic"
            disabled={busy || !topicOptions.length}
            clearQueryOnSelect={false}
          />
        </div>
      ) : null}

      <input
        className="cmt-composer-title"
        type="text"
        value={title}
        placeholder="Title (optional)"
        disabled={busy}
        onChange={(e) => setTitle(e.target.value)}
      />

      <textarea
        ref={ref}
        className="cmt-composer-input"
        rows={1}
        value={text}
        placeholder={placeholder}
        disabled={busy}
        onChange={(e) => {
          setText(e.target.value)
          resize()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
      />

      <div className="cmt-compose-guides">
        <GuidePicker value={guides} onChange={setGuides} />
      </div>

      {files.length ? (
        <div className="cmt-attach-pending">
          {files.map((file, i) => (
            <span key={`${file.name}-${i}`} className="cmt-attach-chip">
              <span className="cmt-attach-chip-name" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="cmt-tag-x"
                title="Remove file"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(i)}
                disabled={busy}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {error ? <div className="cmt-attach-error">{error}</div> : null}

      <div className="cmt-composer-row">
        <button
          type="button"
          className="cmt-chip cmt-attach-btn"
          title="Attach files"
          aria-label="Attach files"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          <IconPaperclip />
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          className={'cmt-chip' + (isQuestion ? ' is-on' : '')}
          onClick={() => setIsQuestion((v) => !v)}
          disabled={busy}
        >
          Question
        </button>
        <button
          type="button"
          className={'cmt-chip' + (isPublic ? ' is-on' : '')}
          onClick={() => setIsPublic((v) => !v)}
          disabled={busy}
        >
          {isPublic ? 'Public' : 'Private'}
        </button>
        <button type="button" className="cmt-post" onClick={send} disabled={busy || !canSend}>
          {busy ? 'Posting…' : submitLabel}
        </button>
      </div>
    </>
  )

  return (
    <div className={'cmt-composer' + (compact ? ' is-compact' : '') + (canCollapse && !open ? ' is-collapsed' : '')}>
      {canCollapse ? (
        <div className="cmt-composer-bar">
          <button
            type="button"
            className="cmt-composer-toggle"
            onClick={toggleOpen}
            aria-expanded={open}
            aria-label={open ? 'Collapse comment form' : 'Expand comment form'}
            title={open ? 'Collapse' : 'Expand'}
          >
            {!open && <span>{text.trim() ? 'Continue comment' : 'Write a comment'}</span>}
            <IconChevron up={!open} />
          </button>
        </div>
      ) : null}
      {open ? fields : null}
    </div>
  )
}
